import classNames from 'classnames';

import {
    GwgAccelerator,
    GwgBanner,
    GwgCarousel,
    useGetCurrentGwGAcceleratorEnrollmentsQuery,
} from '@udemy/career-academies';
import {useI18n} from '@udemy/i18n';
import {
    SubscriptionPlanProductType,
    SubscriptionPlansProvider,
    useActiveSubscriptions,
    useConsumerSubscriptionGwGPlusPersonalPlan,
} from '@udemy/subscription-graphql';

import {
    checkIsGwgAvailable,
    checkIsGwgEnrolled,
    checkIsGwgSubscriber,
    isSubscriptionLoading,
} from 'src/lib/gwg-helpers';

import styles from './home-page.module.less';

export interface GwgSectionProps {
    position: 'top' | 'bottom';
    accelerator?: GwgAccelerator;
}

/**
 * Inner component that uses the subscription hooks (must be inside SubscriptionPlansProvider).
 * - Top position: For GwG subscribers only
 *   - Banner: Always renders for subscribers
 *   - Carousel: Only renders if subscriber is NOT enrolled
 * - Bottom position: Renders GwgCarousel for non-subscribers only
 */
export const GwgSectionInner = ({position, accelerator}: GwgSectionProps) => {
    const {gettext} = useI18n();
    const activeSubscriptions = useActiveSubscriptions();
    const currentlySubscribedPlan = activeSubscriptions?.[0];
    const combinedGwgPlan = useConsumerSubscriptionGwGPlusPersonalPlan();
    const {data: enrollmentData, isLoading: isEnrollmentLoading} =
        useGetCurrentGwGAcceleratorEnrollmentsQuery();

    const isLoading =
        isSubscriptionLoading(currentlySubscribedPlan, combinedGwgPlan) || isEnrollmentLoading;
    const isGwgAvailable = checkIsGwgAvailable(currentlySubscribedPlan, combinedGwgPlan);
    const isGwgSubscriber = checkIsGwgSubscriber(currentlySubscribedPlan, combinedGwgPlan);
    const isGwgEnrolled = checkIsGwgEnrolled(enrollmentData);

    // Don't render if: no accelerator data, still loading, or GwG not available
    if (!accelerator || isLoading || !isGwgAvailable) {
        return null;
    }

    // Top position: For GwG subscribers only
    if (position === 'top') {
        if (!isGwgSubscriber) {
            return null;
        }

        return (
            <div className={classNames('ud-container', styles['gwg-top-wrapper'])}>
                <GwgBanner
                    mainTitle={gettext('Build job ready skills and Learn AI with Google')}
                    subTitle={''}
                    buttonText={gettext('All included content')}
                    buttonHref={'/partner/google/learn-ai/'}
                />
                {/* Carousel only shows if subscriber is NOT enrolled */}
                {!isGwgEnrolled && (
                    <div className={styles['grow-with-google-banner']}>
                        <h2 className="ud-heading-xxl">
                            {gettext('Recommended to build job ready AI skills')}
                        </h2>
                        <GwgCarousel
                            accelerator={accelerator}
                            currentlySubscribedPlan={currentlySubscribedPlan}
                        />
                    </div>
                )}
            </div>
        );
    }

    // Bottom position: Render for non-subscribers only
    if (isGwgSubscriber) {
        return null;
    }

    return (
        <div className={styles['grow-with-google-banner']}>
            <h2 className="ud-heading-xxl">{gettext('Learn AI with Google')}</h2>
            <GwgCarousel
                accelerator={accelerator}
                currentlySubscribedPlan={currentlySubscribedPlan}
            />
        </div>
    );
};

/**
 * Wrapper component that provides SubscriptionPlansProvider context.
 * Availability is determined inside the provider by GwgSectionInner.
 */
export const GwgSection = ({position, accelerator}: GwgSectionProps) => {
    return (
        <SubscriptionPlansProvider
            subscriptionPlanQueryVariables={[
                {filters: {productType: SubscriptionPlanProductType.GrowWithGoogle}},
                {
                    filters: {
                        productType:
                            SubscriptionPlanProductType.ConsumersubscriptionPlusGrowWithGoogle,
                    },
                },
            ]}
        >
            <GwgSectionInner position={position} accelerator={accelerator} />
        </SubscriptionPlansProvider>
    );
};
